import type { TradingPartnerContactEntry } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  Grid,
  HStack,
  Link,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaInboxRegular } from '@enechain/ecloud-icons'

import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'
import { Modal } from '~/components/common/Modal'
import { RegionLoading } from '~/components/common/RegionLoading'
import { useGetTradingPartnerContact } from '~/pages/SubmissionManagement/hooks/useGetTradingPartnerContact'
import { formatContactPerson } from '~/pages/SubmissionManagement/models/contactPerson'
import { routePaths } from '~/routes/routePaths'

const EMPTY_VALUE = '—'

/** 連絡先の SoT である取引先マスタ (広域マスタの取引先タブ)。取引先は特定せず一覧を開く (AC-CM-007)。 */
const TRADING_PARTNER_MASTER_PATH = `${routePaths.adminOccto.path}?tab=trading-partner`

/**
 * 連絡先モーダル (plan-common.md §連絡先モーダル AC-CM-001〜007)。
 *
 * 開いている間だけ mount し、取引先カードの counterparty_code で
 * `GetTradingPartnerContact` を引いて 平日昼間 / 休日・夜間 を分けて表示する。
 * AC-CM-003 に従い footer の「閉じる」と × でのみ閉じ、オーバーレイクリック
 * (共通 Modal の既定) と Escape キーでは閉じない。
 */
export const ContactModal = ({
  counterpartyCode,
  counterpartyName,
  onClose,
}: {
  counterpartyCode: string
  /** 取引先カードの表示名。マスタ未登録 (found=false) で応答に名称が無い時の取引先名。 */
  counterpartyName: string
  onClose: () => void
}): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isPending, isError } =
    useGetTradingPartnerContact(counterpartyCode)

  let body: React.ReactNode
  // 取引先マスタへの導線は 1 か所だけに出す (AC-CM-007): 連絡先 1 件以上 = footer、
  // 0 件 = 空状態の中、取得中・取得失敗 = 件数が分からないので出さない。
  let showFooterMasterLink = false
  if (isPending) {
    body = null
  } else if (isError) {
    body = (
      <Alert
        status="error"
        title={t('page:submissionManagement.contactModal.error')}
      />
    )
  } else {
    // AC-CM-005: 両区分とも連絡先が無い (マスタ未登録 found=false を含む) 時は
    // 区分の表の代わりに空状態を、片方だけ無い時はその区分に区分単位の文言を出す。
    const hasNoContacts =
      !data.found ||
      (data.weekdayContacts.length === 0 && data.holidayContacts.length === 0)
    showFooterMasterLink = !hasNoContacts
    body = hasNoContacts ? (
      <EmptyPanel
        action={
          <TradingPartnerMasterLink
            label={t('page:submissionManagement.contactModal.registerInMaster')}
          />
        }
        description={t(
          'page:submissionManagement.contactModal.emptyDescription',
        )}
        icon={
          <FaInboxRegular boxSize={EMPTY_PANEL_ICON_SIZE} aria-hidden="true" />
        }
        title={t('page:submissionManagement.contactModal.emptyTitle')}
      />
    ) : (
      <VStack alignItems="stretch" gap={4}>
        <ContactSection
          entries={data.weekdayContacts}
          title={t('page:submissionManagement.contactModal.weekday')}
        />
        <ContactSection
          entries={data.holidayContacts}
          title={t('page:submissionManagement.contactModal.holiday')}
        />
      </VStack>
    )
  }

  // 取引先名はマスタ未登録 (found=false)・取得中・取得失敗の時もカードの表示名で出し、
  // どの取引先の連絡先かを示す (AC-CM-001)。
  const partnerName = data?.tradingPartnerName ?? counterpartyName

  return (
    <Modal
      closeOnEscape={false}
      footer={
        <HStack gap={2}>
          {showFooterMasterLink && (
            <TradingPartnerMasterLink
              label={t('page:submissionManagement.contactModal.editInMaster')}
            />
          )}
          <TertiaryButton onClick={onClose}>
            {t('common:action.close')}
          </TertiaryButton>
        </HStack>
      }
      isOpen
      onClose={onClose}
      title={t('page:submissionManagement.contactModal.title', {
        name: partnerName,
      })}
    >
      <RegionLoading isLoading={isPending}>{body}</RegionLoading>
    </Modal>
  )
}

/** 取引先マスタを別タブで開く導線 (AC-CM-007)。内部画面なので external-link icon は付けない (design)。 */
const TradingPartnerMasterLink = ({
  label,
}: {
  label: string
}): React.ReactElement => (
  <TertiaryButton asChild>
    <RouterLink
      rel="noopener noreferrer"
      target="_blank"
      to={TRADING_PARTNER_MASTER_PATH}
    >
      {label}
    </RouterLink>
  </TertiaryButton>
)

const ContactSection = ({
  title,
  entries,
}: {
  title: string
  entries: readonly TradingPartnerContactEntry[]
}): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <VStack as="section" alignItems="stretch" gap={2} aria-label={title}>
      <Text
        textStyle="subSectionTitle"
        paddingBottom={1}
        borderColor="Border.lowEmphasis"
        borderBottomWidth="1px"
      >
        {title}
      </Text>
      {entries.length === 0 ? (
        <Text color="Text.lowEmphasis" fontSize="sm">
          {t('page:submissionManagement.contactModal.noEntries')}
        </Text>
      ) : (
        entries.map((entry) => (
          <FieldRows
            key={entry.id}
            rows={[
              {
                label: t('page:submissionManagement.contactModal.contactName'),
                value: formatContactPerson(entry),
              },
              {
                label: t('page:submissionManagement.contactModal.phone'),
                value: entry.phone,
                // AC-CM-002: 業務端末の PC からは発信できず、リンクの見た目が遷移と誤認させる
                // ため、電話はリンクにせず強調テキストで出す。
                emphasized: true,
              },
              {
                label: t('page:submissionManagement.contactModal.email'),
                value: entry.email,
                href:
                  entry.email !== undefined && entry.email !== ''
                    ? `mailto:${entry.email}`
                    : undefined,
              },
            ]}
          />
        ))
      )}
    </VStack>
  )
}

/** ラベル / 値の 2 列。値が空なら「—」を出し、リンクにも強調にもしない。 */
const FieldRows = ({
  rows,
}: {
  rows: ReadonlyArray<{
    label: string
    value: string | undefined
    href?: string
    emphasized?: boolean
  }>
}): React.ReactElement => (
  <Grid
    alignItems="baseline"
    gridRowGap={1}
    gridColumnGap={4}
    gridTemplateColumns="auto 1fr"
  >
    {rows.map(({ label, value, href, emphasized }) => (
      <FieldRow
        key={label}
        emphasized={emphasized === true}
        href={href}
        label={label}
        value={value}
      />
    ))}
  </Grid>
)

const FieldRow = ({
  label,
  value,
  href,
  emphasized,
}: {
  label: string
  value: string | undefined
  href: string | undefined
  emphasized: boolean
}): React.ReactElement => {
  const hasValue = value !== undefined && value !== ''
  return (
    <>
      <Text color="Text.midEmphasis" fontSize="sm">
        {label}
      </Text>
      {href !== undefined ? (
        <Link color="Text.accentPrimary" fontSize="sm" href={href}>
          {value}
        </Link>
      ) : hasValue && emphasized ? (
        <Text
          textStyle="contactPhone"
          wordBreak="break-all"
          fontVariantNumeric="tabular-nums"
        >
          {value}
        </Text>
      ) : (
        <Text fontSize="sm" wordBreak="break-all">
          {hasValue ? value : EMPTY_VALUE}
        </Text>
      )}
    </>
  )
}
